/**
 * Predictive Search — FIGS-style instant search modal
 * Uses Shopify's section-rendering endpoint: /search/suggest?section_id=predictive-search-results
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 300;
  const RESULTS_LIMIT = 6;
  const SECTION_ID = 'predictive-search-results';

  // DOM refs
  const overlay = document.querySelector('[data-ps-overlay]');
  const modal = document.querySelector('[data-ps-modal]');
  const input = document.querySelector('[data-ps-input]');
  const resultsContainer = document.querySelector('[data-ps-results]');
  const popularContainer = document.querySelector('[data-ps-popular]');
  const loadingEl = document.querySelector('[data-ps-loading]');
  const closeBtn = document.querySelector('[data-ps-close]');
  const toggleBtns = document.querySelectorAll('[data-ps-toggle]');

  if (!modal || !input) return;

  let debounceTimer = null;
  let abortController = null;
  let isOpen = false;

  // ── Open / Close ──

  function open() {
    if (isOpen) return;
    isOpen = true;
    modal.classList.add('is-open');
    overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    input.value = '';
    resultsContainer.innerHTML = '';
    popularContainer.style.display = '';
    loadingEl.style.display = 'none';
    // Small delay so the transition finishes before focus
    requestAnimationFrame(function () {
      input.focus();
    });
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    modal.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  // ── Event listeners: open/close ──

  toggleBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
  });

  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) close();
  });

  // ── Predictive Search API ──

  function fetchResults(query) {
    var trimmed = query.trim();

    if (!trimmed) {
      resultsContainer.innerHTML = '';
      popularContainer.style.display = '';
      loadingEl.style.display = 'none';
      return;
    }

    popularContainer.style.display = 'none';
    loadingEl.style.display = '';

    // Cancel any in-flight request
    if (abortController) abortController.abort();
    abortController = new AbortController();

    var url =
      '/search/suggest?q=' +
      encodeURIComponent(trimmed) +
      '&resources[type]=product,query' +
      '&resources[limit]=' +
      RESULTS_LIMIT +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,tag' +
      '&section_id=' +
      SECTION_ID;

    fetch(url, { signal: abortController.signal })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (text) {
        loadingEl.style.display = 'none';
        var parser = new DOMParser();
        var doc = parser.parseFromString(text, 'text/html');
        var inner = doc.querySelector('#predictive-search-results-inner');
        resultsContainer.innerHTML = inner ? inner.innerHTML : '';

        // Swap thumbnails to variant-matched images from JSON API
        // Shopify section rendering uses product.featured_image (default image),
        // but the JSON API returns the correct variant-matched featured_image.
        var jsonUrl = '/search/suggest.json?q=' + encodeURIComponent(trimmed) +
          '&resources[type]=product&resources[limit]=' + RESULTS_LIMIT +
          '&resources[options][unavailable_products]=hide';

        fetch(jsonUrl, { signal: abortController.signal })
          .then(function(r) { return r.json(); })
          .then(function(data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            var imageMap = {};
            products.forEach(function(p) {
              if (p.featured_image && p.featured_image.url) {
                // Extract color from alt text (format: "COLORNAME - Image N")
                var colorName = (p.featured_image.alt || '').split(' - ')[0].trim();
                imageMap[p.handle] = { url: p.featured_image.url, color: colorName };
              }
            });
            resultsContainer.querySelectorAll('.ps-results__item').forEach(function(item) {
              var href = item.getAttribute('href') || '';
              var handleMatch = href.match(/\/products\/([^?]+)/);
              if (!handleMatch) return;
              var entry = imageMap[handleMatch[1]];
              if (!entry) return;
              // Swap thumbnail image (handle-keyed, no URL rewrite)
              var img = item.querySelector('.ps-results__image');
              if (img) {
                var sep = entry.url.includes('?') ? '&' : '?';
                img.src = entry.url + sep + 'width=200';
                img.srcset = entry.url + sep + 'width=200 1x, ' + entry.url + sep + 'width=400 2x';
              }
              // NOTE: Do NOT rewrite href to ?color=. The Liquid section now emits
              // ?variant=ID so the PDP lands on the exact variant whose price the
              // card displays — replacing it with ?color= broke price/PDP coupling.
              // See #95.
            });
          })
          .catch(function(err) {
            if (err.name === 'AbortError') return;
          });

        // #152: Preserve ?variant= on product link clicks.
        // Shopify's analytics JS (from content_for_header) intercepts search
        // result clicks and navigates with tracking params, dropping our
        // ?variant= param. Handle the click ourselves to ensure the PDP
        // lands on the correct variant with matching price.
        resultsContainer.querySelectorAll('.ps-results__item').forEach(function (item) {
          var href = item.getAttribute('href') || '';
          if (href.includes('variant=')) {
            item.addEventListener('click', function (e) {
              e.preventDefault();
              e.stopImmediatePropagation();
              close();
              window.location.href = href;
            });
          }
        });

        // Wire up suggestion clicks to populate the search input
        resultsContainer.querySelectorAll('[data-suggestion-text]').forEach(function (link) {
          link.addEventListener('click', function (e) {
            e.preventDefault();
            var term = link.getAttribute('data-suggestion-text');
            input.value = term;
            fetchResults(term);
          });
        });
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        loadingEl.style.display = 'none';
        resultsContainer.innerHTML =
          '<p class="ps-results__empty">Something went wrong. Please try again.</p>';
      });
  }

  // ── Input handling ──

  input.addEventListener('input', function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      fetchResults(input.value);
    }, DEBOUNCE_MS);
  });

  // Submit form → go to full search results page
  var form = input.closest('form');
  if (form) {
    form.addEventListener('submit', function () {
      close();
    });
  }

  // ── Popular search clicks → populate input and search ──

  document.querySelectorAll('[data-popular-search]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      e.preventDefault();
      var term = link.textContent.trim();
      input.value = term;
      fetchResults(term);
    });
  });
})();
